import { useState, useRef, useCallback } from 'react'
import { RouteData } from '@cya-routes/shared'
import { RouteFlyby } from '../../../utils/routeFlyby'

interface UseFlybyControlsProps {
  mapManager: React.MutableRefObject<any>
  getCurrentRoute: () => RouteData | null
  selectedRouteId: string
  zoomToRoute: (routeId: string) => void
  is3DEnabled: boolean
  setIs3DEnabled: (enabled: boolean) => void
  routeRenderer?: React.MutableRefObject<any>
  poiRenderer?: React.MutableRefObject<any>
}

interface UseFlybyControlsReturn {
  flybyState: 'stopped' | 'playing' | 'paused'
  flybyProgress: number
  flybySpeed: number
  startFlyby: () => void
  pauseFlyby: () => void
  resumeFlyby: () => void
  stopFlyby: () => void
  changeSpeed: (speed: number) => void
  flybyRef: React.MutableRefObject<RouteFlyby | null>
}

/**
 * Custom hook that manages 3D flyby functionality
 * Handles flyby state, controls, and progress tracking
 */
export function useFlybyControls({
  mapManager,
  getCurrentRoute,
  selectedRouteId,
  zoomToRoute,
  is3DEnabled,
  setIs3DEnabled,
  routeRenderer,
  poiRenderer
}: UseFlybyControlsProps): UseFlybyControlsReturn {
  const [flybyState, setFlybyState] = useState<'stopped' | 'playing' | 'paused'>('stopped')
  const [flybyProgress, setFlybyProgress] = useState(0)
  const [flybySpeed, setFlybySpeed] = useState(90) // km/minute - default to 90km/min
  const flybyRef = useRef<RouteFlyby | null>(null)
  
  // Start flyby
  const startFlyby = useCallback(async () => {
    console.log('🎮 useFlybyControls - startFlyby called')
    const currentRoute = getCurrentRoute()
    if (!currentRoute || !mapManager.current) {
      console.log('🎮 No current route or map manager')
      return
    }

    const map = mapManager.current.getMap()
    if (!map) {
      console.log('🎮 No map instance')
      return
    }

    // Initialize flyby if needed
    if (!flybyRef.current) {
      console.log('🎮 Creating new RouteFlyby instance')
      flybyRef.current = new RouteFlyby(map)
    }

    // Enable 3D if not already
    if (!is3DEnabled) {
      console.log('🎮 3D not enabled, enabling it now')
      setIs3DEnabled(true)
      if (!map.getSource('mapbox-dem')) {
        console.log('🎮 Adding mapbox-dem source')
        map.addSource('mapbox-dem', {
          type: 'raster-dem',
          url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
          tileSize: 512,
          maxzoom: 14
        })
      }
      console.log('🎮 Setting terrain in useFlybyControls')
      map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 })
    } else {
      console.log('🎮 3D already enabled')
    }

    // Highlight all climbs at the start of the tour
    if (routeRenderer?.current && currentRoute.elevation?.profile) {
      // Get climbs from the route
      const { ClimbDetectionService } = await import('../../../services/climbDetectionService')
      const climbs = currentRoute.elevation?.climbs || ClimbDetectionService.detectClimbs(currentRoute.elevation.profile)
      
      // Highlight all climbs
      if (climbs && climbs.length > 0) {
        routeRenderer.current.highlightClimbs(selectedRouteId, climbs, currentRoute)
      }
    }

    // Notify POI renderer that flyby is starting
    if (poiRenderer?.current) {
      poiRenderer.current.setFlybyActive(true)
    }

    // Start the flyby
    flybyRef.current.start(currentRoute, {
      speed: flybySpeed, // Use current speed setting
      altitude: 1500, // 1.5km altitude for wider view
      pitch: 70, // 70 degree pitch for dramatic 3D view
      bearing: 'auto', // Auto-follow route direction
      showClimbs: false // Disable dynamic climb highlighting since we're showing all climbs
    }, {
      onProgress: (progress) => {
        setFlybyProgress(progress * 100)
      },
      onComplete: () => {
        setFlybyState('stopped')
        setFlybyProgress(0)
        // Clear climb highlighting
        if (routeRenderer?.current) {
          routeRenderer.current.clearClimbHighlights()
        }
        // Notify POI renderer that flyby ended
        if (poiRenderer?.current) {
          poiRenderer.current.setFlybyActive(false)
        }
        // Return to normal view but keep 3D
        const map = mapManager.current?.getMap()
        if (map) {
          // Ensure 3D stays enabled
          map.setPitch(30) // Reset to default 3D pitch
        }
        zoomToRoute(selectedRouteId || currentRoute.id)
      }
    })

    setFlybyState('playing')
  }, [getCurrentRoute, selectedRouteId, is3DEnabled, flybySpeed, zoomToRoute, mapManager, setIs3DEnabled, routeRenderer, poiRenderer])

  // Pause flyby
  const pauseFlyby = useCallback(() => {
    if (flybyRef.current) {
      flybyRef.current.pause()
      setFlybyState('paused')
    }
  }, [])

  // Resume flyby
  const resumeFlyby = useCallback(() => {
    if (flybyRef.current) {
      flybyRef.current.resume()
      setFlybyState('playing')
    }
  }, [])

  // Stop flyby
  const stopFlyby = useCallback(() => {
    if (flybyRef.current) {
      flybyRef.current.stop()
      setFlybyState('stopped')
      setFlybyProgress(0)
      // Clear climb highlighting
      if (routeRenderer?.current) {
        routeRenderer.current.clearClimbHighlights()
      }
      // Notify POI renderer that flyby ended
      if (poiRenderer?.current) {
        poiRenderer.current.setFlybyActive(false)
      }
      // Return to normal view but ensure 3D stays enabled
      const map = mapManager.current?.getMap()
      if (map) {
        // Use easeTo for smooth animation on mobile
        map.easeTo({
          pitch: 30, // Ensure 3D pitch
          duration: 800,
          essential: true // Force animation on mobile devices
        })
      }
      const currentRoute = getCurrentRoute()
      if (currentRoute) {
        // Small delay to ensure the pitch animation completes first
        setTimeout(() => {
          zoomToRoute(selectedRouteId || currentRoute.id)
        }, 100)
      }
    }
  }, [getCurrentRoute, selectedRouteId, zoomToRoute, mapManager, routeRenderer, poiRenderer])

  // Change speed
  const changeSpeed = useCallback((newSpeed: number) => {
    setFlybySpeed(newSpeed)
    if (flybyRef.current && flybyState !== 'stopped') {
      flybyRef.current.setSpeed(newSpeed)
    }
  }, [flybyState])
  
  return {
    flybyState,
    flybyProgress,
    flybySpeed,
    startFlyby,
    pauseFlyby,
    resumeFlyby,
    stopFlyby,
    changeSpeed,
    flybyRef
  }
}